import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Search, FlaskConical, Loader2, Building2, Star, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export default function ContactMatchingPage() {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const autoMatchedRef = React.useRef(false);

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/pharma-contacts?limit=100000`, { withCredentials: true });
      const list = res.data.contacts || [];
      setContacts(list);

      // Auto-match from URL param after contacts load
      const contactId = searchParams.get('contact');
      if (contactId && !autoMatchedRef.current) {
        const c = list.find(x => x.id === contactId);
        if (c) {
          autoMatchedRef.current = true;
          setSelectedContact(c);
          setLoading(true);
          try {
            const matchRes = await axios.post(`${API}/ai/match-contact-assets`, { contact_id: c.id }, { withCredentials: true });
            setMatches(matchRes.data);
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Matching failed');
          } finally { setLoading(false); }
        }
      }
    } catch { toast.error('Failed to load contacts'); }
    finally { setContactsLoading(false); }
  };

  useEffect(() => {
    fetchContacts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMatch = async (contact) => {
    setSelectedContact(contact);
    setMatches(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/match-contact-assets`, { contact_id: contact.id }, { withCredentials: true });
      setMatches(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Matching failed');
    } finally { setLoading(false); }
  };

  const filtered = contacts.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) ||
      (c.company_name || '').toLowerCase().includes(s) ||
      (c.email || '').toLowerCase().includes(s);
  });

  const contactsWithPrefs = contacts.filter(c => (c.preferred_stages?.length > 0 || c.interest_areas?.length > 0));
  const contactsWithoutPrefs = contacts.filter(c => !c.preferred_stages?.length && !c.interest_areas?.length);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="contact-matching-page">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Contact Matching</h1>
        <p className="text-sm text-slate-500 mt-1">Match pharma contacts to biotech assets based on their therapeutic areas and preferred stages</p>
      </div>

      <div className="grid grid-cols-[380px_1fr] gap-6">
        {/* Left: Contact List */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="text-xs font-medium text-slate-500">
              <span className="text-emerald-600">{contactsWithPrefs.length}</span> with preferences |
              <span className="text-slate-400 ml-1">{contactsWithoutPrefs.length}</span> without
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input className="pl-9" placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} data-testid="contact-match-search" />
          </div>
          <div className="max-h-[calc(100vh-240px)] overflow-y-auto space-y-1 pr-1">
            {contactsLoading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-8">No contacts found</p>
            ) : filtered.map(c => (
              <button
                key={c.id}
                onClick={() => handleMatch(c)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selectedContact?.id === c.id
                    ? 'border-sky-300 bg-sky-50'
                    : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`contact-card-${c.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{c.name}</p>
                    <p className="text-[10px] text-slate-500 truncate">{c.company_name}{c.designation ? ` - ${c.designation}` : ''}</p>
                  </div>
                  {(c.preferred_stages?.length > 0 || c.interest_areas?.length > 0) && (
                    <div className="shrink-0 w-2 h-2 rounded-full bg-emerald-400 mt-1" title="Has preferences" />
                  )}
                </div>
                {c.preferred_stages?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {c.preferred_stages.map((s, i) => (
                      <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-600 border border-sky-100">{s}</span>
                    ))}
                  </div>
                )}
                {c.interest_areas?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {c.interest_areas.map((a, i) => (
                      <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 border border-violet-100">{a}</span>
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Right: Match Results */}
        <div>
          {!selectedContact ? (
            <Card className="border border-slate-200">
              <CardContent className="py-24 text-center">
                <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-400">Select a pharma contact to see matched assets</p>
              </CardContent>
            </Card>
          ) : loading ? (
            <Card className="border border-slate-200">
              <CardContent className="py-24 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-sky-500 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Matching assets for {selectedContact.name}...</p>
              </CardContent>
            </Card>
          ) : matches ? (
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {matches.contact}
                    </h2>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {matches.preferred_stages?.map((s, i) => (
                        <Badge key={i} className="text-[9px] bg-sky-50 text-sky-700 border border-sky-200">{s}</Badge>
                      ))}
                      {matches.interest_areas?.map((a, i) => (
                        <Badge key={i} className="text-[9px] bg-violet-50 text-violet-700 border border-violet-200">{a}</Badge>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-slate-900">{matches.total_matches}</p>
                    <p className="text-[10px] text-slate-500">matching assets</p>
                    {matches.stage_only_additional > 0 && (
                      <p className="text-[10px] text-amber-600">+{matches.stage_only_additional} stage-only matches</p>
                    )}
                  </div>
                </div>
                {matches.message && (
                  <div className="mt-3 bg-amber-50 border border-amber-200 rounded p-2">
                    <p className="text-xs text-amber-700">{matches.message}</p>
                  </div>
                )}
              </div>

              {matches.matches.length > 0 ? (
                <Card className="border border-slate-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table data-testid="match-results-table">
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Asset</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Company</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Stage</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Indication</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Target</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2">Modality</TableHead>
                          <TableHead className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 py-2 w-[40px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {matches.matches.map((m, i) => (
                          <TableRow key={i} className="hover:bg-slate-50/50">
                            <TableCell className="py-2 text-xs font-medium text-slate-800">{m.asset_name || '—'}</TableCell>
                            <TableCell className="py-2">
                              <div className="flex items-center gap-1">
                                {m.is_my_company && <Star className="w-3 h-3 fill-amber-400 text-amber-400" />}
                                <span className="text-xs text-slate-700">{m.company_name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="py-2">
                              <Badge className="text-[9px] bg-sky-50 text-sky-700 border border-sky-200">{m.stage}</Badge>
                            </TableCell>
                            <TableCell className="py-2 text-[11px] text-slate-600">{m.indication || '—'}</TableCell>
                            <TableCell className="py-2 text-[11px] text-slate-600">{m.target || '—'}</TableCell>
                            <TableCell className="py-2 text-[11px] text-slate-600">{m.modality || '—'}</TableCell>
                            <TableCell className="py-2">
                              <a href={`/biotech/${m.company_id}`} target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:text-sky-700">
                                <ArrowRight className="w-3.5 h-3.5" />
                              </a>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </Card>
              ) : (
                <Card className="border border-slate-200">
                  <CardContent className="py-12 text-center">
                    <FlaskConical className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm text-slate-400">No matching assets found</p>
                    <p className="text-xs text-slate-400 mt-1">Try broadening the contact's preferred stages or therapeutic areas</p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
